<template>
  <div>
    <NuxtLayout name="default">
      <!-- Hero section -->
      <section class="section-hero" :style="getBgImage($page?.aboutUs?.heroImage!)">
        <div class="container px-0">
          <div class="row gx-0">
            <div
              class="col-12 d-flex flex-column align-items-start justify-content-end section-hero-content"
            >
              <div class="py-space-md py-md-space-xl">
                <div class="mb-md-space-md px-space-xs px-md-0">
                  <h1
                    class="h1-large fw-bold text-white py-space-xxs px-space-xs d-inline-block bg-dark-3"
                    tabindex="0"
                  >
                    {{ $page?.aboutUs?.heroTitle }}
                  </h1>
                </div>

                <div class="d-none d-md-block mb-space-xxs">
                  <p
                    class="fs-xl fw-bold text-secondary p-space-xxs d-inline-block bg-dark-3"
                    tabindex="0"
                  >
                    {{ $page?.aboutUs?.heroSubtitle }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- What is ASU Online like section -->
      <section class="py-space-lg pb-lg-0 pt-lg-space-xxl bg-light-2">
        <div class="container px-space-sm px-md-0">
          <div class="row">
            <div
              class="col-12 offset-lg-1 col-lg-10 d-flex flex-column flex-lg-row p-space-md pt-md-space-xl ps-md-space-xl asu-online-like-bg"
              :style="getBgImage($page?.aboutUs?.asuoLikeBgImage!)"
            >
              <div>
                <h2 class="h2-medium mb-space-sm" tabindex="0">
                  {{ $page?.aboutUs?.asuoLikeTitle }}
                </h2>
                <div
                  class="fs-medium mb-space-lg"
                  tabindex="0"
                  v-html="$page?.aboutUs?.asuoLikeDescription"
                ></div>

                <div class="my-space-xs pe-md-space-xxl">
                  <div
                    v-for="eachItem in $page?.aboutUs?.asuoLikeStatItems"
                    :key="eachItem?.text!"
                    class="d-flex mb-space-md flex-column flex-md-row"
                    tabindex="0"
                  >
                    <div class="d-lg-flex align-items-center">
                      <h3
                        class="h3-xl bg-secondary py-space-xxs px-space-xs me-space-xs d-inline-block"
                      >
                        {{ eachItem?.title }}
                      </h3>
                      <p class="fw-bold fs-xl mb-0 wrap-text">
                        {{ eachItem?.text }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="d-none d-lg-block ms-md-space-sm">
                <img
                  :alt="$page?.aboutUs?.asuoLikeTitle!"
                  class="asu-online-like-img"
                  :src="$page?.aboutUs?.asuoLikeImage!"
                  loading="lazy"
                  width="1018"
                  height="916"
                />
              </div>
            </div>
          </div>
          <div class="row d-block d-lg-none">
            <div class="col-12">
              <div class="text-center">
                <img
                  :alt="$page?.aboutUs?.asuoLikeTitle!"
                  class="asu-online-like-img"
                  :src="$page?.aboutUs?.asuoLikeImage!"
                  loading="lazy"
                  width="1018"
                  height="916"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ASU responsibility section -->
      <section class="py-space-lg py-lg-space-xxl bg-light-2 asu-responsibility">
        <div class="container">
          <div class="row">
            <div class="col-12">
              <h2 class="h2-medium mb-space-sm text-center" tabindex="0">
                {{ $page?.aboutUs?.responsibilityTitle }}
              </h2>
              <div class="mb-space-md text-center">
                <h3
                  class="h3-medium text-dark-3 d-inline-block bg-secondary py-space-xxs px-space-xxs px-md-space-xs"
                  tabindex="0"
                >
                  {{ $page?.aboutUs?.responsibilitySubtitle }}
                </h3>
              </div>
            </div>
          </div>

          <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3">
            <div
              v-for="(card, index) in $page?.aboutUs?.responsibilityCards"
              :key="index"
              class="col mb-space-md card-icon-wrap"
              tabindex="0"
            >
              <CardIcon
                class="bg-dark-3 h-100"
                :icon-source="card?.icon!"
                :icon-alt="card?.iconAlt!"
                :display-border="false"
                :title="card?.title!"
                title-variant="white"
                :display-cta="false"
                card-background-variant="dark-3"
              >
                <div class="text-white" v-html="card?.description"></div>
              </CardIcon>
            </div>
          </div>
          <EBook
            :text="$page?.aboutUs?.ebookText!"
            :btn-text="$page?.aboutUs?.ebookButton?.text!"
            :e-book-url="$page?.aboutUs?.ebookButton?.uri!"
            @btn-click="handleEbookBtnClick"
          ></EBook>
        </div>
      </section>

      <!-- Dedicated to your success section -->
      <section class="py-space-lg py-lg-space-xxl">
        <div class="container">
          <VideoModalAtlas
            :video-source="getVideoSource"
            :bg-image-source="$page?.aboutUs?.dedicatedToSuccess?.imageSource!"
            :title="$page?.aboutUs?.dedicatedToSuccess?.title!"
            :title-focusable="true"
            @modal-open="handleVideoModalOpen"
            @modal-close="handleVideoModalClose"
          >
            <div tabindex="0" v-html="$page?.aboutUs?.dedicatedToSuccess?.description"></div>
          </VideoModalAtlas>
        </div>
      </section>

      <!-- Why choose ASU section -->
      <section id="why-choose-asuo" class="py-space-lg py-lg-space-xxl">
        <div class="container">
          <div class="row">
            <div class="col-12 col-lg-10 offset-lg-1">
              <h2 class="h2-medium mb-space-sm" tabindex="0">{{ $page?.aboutUs?.whyAsuoTitle }}</h2>
              <div
                class="fs-large mb-space-md"
                tabindex="0"
                v-html="$page?.aboutUs?.whyAsuoDescription"
              ></div>
            </div>
            <div class="col-12 col-lg-10 offset-lg-1 px-0">
              <RankingCarouselApollo
                v-if="whyAsuoRankingItems.length > 0"
                :background-image="rankingCarouselBgImage"
                background-image-alt=""
                :items="whyAsuoRankingItems"
                title-size="small"
                text-focusable
                @play-pause-click="handlePlayPauseBtnClick"
                @previous-clicked="(num: number) => handleCarouselChevronClick(num, 'PREV')"
                @next-clicked="(num: number) => handleCarouselChevronClick(num, 'NEXT')"
              ></RankingCarouselApollo>
            </div>
          </div>
        </div>
      </section>

      <!-- How does ASU Online work section -->
      <SectionTabsAsuOnline
        v-if="sectionTabsData.tabs.length > 0"
        class="mb-0 mb-lg-space-xxl"
        :data="sectionTabsData"
        @tab-clicked="onTabChange"
      ></SectionTabsAsuOnline>

      <!-- Is ASU online good school section -->
      <SectionGridAtlas :display-title-underline="false" :match-heights="false">
        <template #header>
          <h2 class="h2-medium text-dark-3 pb-space-sm" tabindex="0">
            {{ $page?.aboutUs?.isAsuoGoodTitle }}
          </h2>
          <div class="row">
            <div
              class="col-12 col-lg-8 mb-space-lg"
              tabindex="0"
              v-html="$page?.aboutUs?.isAsuoGoodDescription"
            ></div>
          </div>
        </template>
        <template #grid-items>
          <div
            v-for="(item, index) in $page?.aboutUs?.isAsuoGoodRankings"
            :key="index"
            tabindex="0"
          >
            <p
              class="bg-secondary fs-xl fw-bold mb-space-xs d-inline-block py-space-xxxs px-space-xxs"
            >
              {{ item?.title }}
            </p>
            <h3 class="fs-medium fw-bold mb-space-xs">{{ item?.text }}</h3>
            <p class="fs-xs mb-0">{{ item?.subText }}</p>
          </div>
        </template>
      </SectionGridAtlas>

      <!-- ASU Online acreditation -->
      <section
        class="section-bg py-space-lg py-lg-space-xxl rds-lazy"
        :data-background-image="$page?.aboutUs?.accreditationImage"
      >
        <div class="container">
          <div class="row gx-0">
            <div class="col-12 offset-lg-1 col-lg-10">
              <CardInfoHorizontal
                :bg-image-source="($page?.aboutUs?.accreditation?.image || undefined) as string"
                :image-fit="$page?.aboutUs?.accreditation?.image ? 'cover' : 'contain'"
                border-variant="light-4"
                :title="$page?.aboutUs?.accreditation?.title!"
                title-level="h2"
                title-size="small"
                title-focusable
                :display-label="false"
                :cta-as-button="true"
                cta-button-variant="secondary"
                :cta-text="$page?.aboutUs?.accreditation?.link?.text!"
                cta-text-size="medium"
                cta-text-variant="dark-3"
                cta-text-weight="bold"
                :cta-link="$page?.aboutUs?.accreditation?.link?.uri!"
                :cta-link-target="formatLinkTarget($page?.aboutUs?.accreditation?.link?.target!)"
                @cta-click="handleAccreditationCtaClick"
              >
                <div
                  class="mb-space-sm"
                  tabindex="0"
                  v-html="$page?.aboutUs?.accreditation?.text"
                ></div>
              </CardInfoHorizontal>
            </div>
          </div>
        </div>
      </section>
      <!-- Faculty excellence section -->
      <SectionOverlapApollo
        :image-source="$page?.aboutUs?.facultyExcellence?.image!"
        :image-alt="$page?.aboutUs?.facultyExcellence?.imageAlt!"
        image-position="right"
        :title="$page?.aboutUs?.facultyExcellence?.title!"
        title-variant="dark-3"
        title-focusable
        container-bg-color="light-3"
        section-bg-color="light-3"
        :display-cta="true"
        :cta-text="$page?.aboutUs?.facultyExcellence?.link?.text!"
        :cta-link="$page?.aboutUs?.facultyExcellence?.link?.uri!"
        :cta-link-target="formatLinkTarget($page?.aboutUs?.facultyExcellence?.link?.target!)"
        cta-text-variant="primary"
        class="intro-section mt-lg-space-xxl mb-lg-space-xl"
        @cta-click="
          handleInternalLinkCtaClick(
            $event,
            $page?.aboutUs?.facultyExcellence?.title!,
            $page?.aboutUs?.facultyExcellence?.link?.text!,
            $page?.aboutUs?.facultyExcellence?.link?.uri!
          )
        "
      >
        <template #body>
          <div
            class="text-dark-3"
            tabindex="0"
            v-html="$page?.aboutUs?.facultyExcellence?.text"
          ></div>
        </template>
      </SectionOverlapApollo>

      <!-- Building the future Section -->
      <section class="building-future py-space-lg pt-lg-0 pb-lg-space-xxl bg-dark-3">
        <div class="container">
          <div class="row gx-0">
            <div class="col-12">
              <div class="mb-space-md mb-lg-space-xl">
                <img
                  class="building-future-hero-img"
                  :src="$page?.aboutUs?.futureAsuoImage!"
                  :alt="$page?.aboutUs?.futureAsuoTitle!"
                  loading="lazy"
                  width="1109"
                  height="550"
                />
              </div>

              <h2
                class="h2-medium mb-space-sm text-white"
                tabindex="0"
                v-html="$page?.aboutUs?.futureAsuoTitle"
              ></h2>

              <div
                class="mb-space-lg text-white"
                tabindex="0"
                v-html="$page?.aboutUs?.futureAsuoDescription"
              ></div>
            </div>
          </div>

          <div class="row">
            <div
              v-for="(eachCard, index) in $page?.aboutUs?.futureAsuoCards"
              :key="index"
              class="col-12 col-lg-3 px-space-xs mb-space-lg mb-lg-0"
              tabindex="0"
            >
              <img
                class="mb-space-sm"
                loading="lazy"
                width="255"
                height="255"
                :src="eachCard?.image!"
                :alt="eachCard?.imageAlt!"
              />

              <div class="text-white mb-0" v-html="eachCard?.caption"></div>
            </div>
          </div>
        </div>
      </section>

      <!-- e-book download section -->
      <SectionIntroFalcon
        :title="$page?.aboutUs?.ebookSectionTitle!"
        :image-source="$page?.aboutUs?.ebookImage!"
        section-background-variant="light-3"
        container-bg-color="light-1"
      >
        <template #body>
          <div
            class="mb-space-sm"
            tabindex="0"
            v-html="$page?.aboutUs?.ebookSectionDescription"
          ></div>
          <NuxtLink
            :href="$page?.aboutUs?.ebookSectionCta?.uri!"
            :target="$page?.aboutUs?.ebookSectionCta?.target!"
            class="btn btn-secondary px-space-md"
            @click="handleEbookSectionLinkClick"
          >
            <font-awesome-icon icon="download" class="me-space-xxs"></font-awesome-icon>
            <span> {{ $page?.aboutUs?.ebookSectionCta?.text! }}</span>
          </NuxtLink>
        </template>
      </SectionIntroFalcon>

      <!-- section-rfi-multi-step -->
      <SectionRfiMultiStep :bg-image="RfiBgImage"></SectionRfiMultiStep>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { CardIcon } from "@rds-vue-ui/card-icon";
import { CardInfoHorizontal } from "@rds-vue-ui/card-info-horizontal";
import { RankingCarouselApollo } from "@rds-vue-ui/ranking-carousel-apollo";
import { SectionGridAtlas } from "@rds-vue-ui/section-grid-atlas";
import { SectionIntroFalcon } from "@rds-vue-ui/section-intro-falcon";
import { SectionOverlapApollo } from "@rds-vue-ui/section-overlap-apollo";
import { VideoModalAtlas } from "@rds-vue-ui/video-modal-atlas";
import { htmlToText } from "html-to-text";
import rankingCarouselBgImage from "~/assets/img/about-us/why-asuo-carousel-bg.jpg";
import whyCard1 from "~/assets/img/about-us/why-card-1.png";
import whyCard2 from "~/assets/img/about-us/why-card-2.png";
import whyCard3 from "~/assets/img/about-us/why-card-3.png";
import whyCard4 from "~/assets/img/about-us/why-card-4.png";
import RfiBgImage from "~/assets/img/rfi-bg-overlay.png";
import type { Data } from "~/components/about-us/SectionTabsAsuOnline.vue";
import SectionTabsAsuOnline from "~/components/about-us/SectionTabsAsuOnline.vue";
import type { EbookClickEventPayload } from "~/components/EBook.vue";
import EBook from "~/components/EBook.vue";
import SectionRfiMultiStep from "~/components/rfi/SectionRfiMultiStep.vue";
import { useUtilityService } from "~/composables/useUtilityService";
import type { AboutUsQuery } from "@/types/graphql-operations";
import type { AboutUs } from "~/server/resolvers/resolvers-types";

definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"]
  }
});

const config = useRuntimeConfig();

const UtilityService = useUtilityService();

const sectionTabsData = ref<Data>({
  title: "",
  text: "",
  bgImage: "",
  tabs: []
});

const $page = await usePageQuery<AboutUsQuery>(
  /* GraphQL */ `
    #graphql
    query aboutUs($aboutUsId: ID!) {
      aboutUs(id: $aboutUsId) {
        metaTitle
        metaDescription
        detailPage
        heroTitle
        heroSubtitle
        heroImage
        asuoLikeTitle
        asuoLikeDescription
        asuoLikeBgImage
        asuoLikeImage
        asuoLikeStatItems {
          title
          text
        }
        responsibilityTitle
        responsibilitySubtitle
        responsibilityCards {
          title
          description
          icon
          iconAlt
        }
        whyAsuoTitle
        whyAsuoDescription
        whyAsuoCards {
          title
          description
          icon
          iconAlt
        }
        howAsuoTitle
        howAsuoDescription
        howAsuoImage
        howAsuoTabs {
          title
          description
          icon
          iconAlt
        }
        isAsuoGoodTitle
        isAsuoGoodDescription
        isAsuoGoodRankings {
          title
          text
          subText
        }
        dedicatedToSuccess {
          title
          description
          youtubeId
          imageSource
          imageAlt
          uploadDate
          duration
          captionText
          date
        }
        accreditationImage
        accreditation {
          title
          image
          imageAlt
          text
          link {
            text
            uri
            target
          }
        }
        facultyExcellence {
          title
          image
          imageAlt
          text
          link {
            text
            uri
            target
          }
        }
        futureAsuoTitle
        futureAsuoDescription
        futureAsuoImage
        futureAsuoCards {
          image
          imageAlt
          caption
        }
        ebookSectionTitle
        ebookSectionDescription
        ebookImage
        ebookSectionCta {
          text
          uri
          target
        }
        ebookText
        ebookButton {
          text
          uri
          target
        }
      }
    }
  `,
  {
    aboutUsId: "21e4ec73-4e57-4075-b3a7-a47d09b1b2c9"
  },
  {
    key: "about-us-index"
  }
);

useHead({
  title: $page?.aboutUs?.metaTitle,
  meta: [
    { name: "Title", content: $page?.aboutUs?.metaTitle },
    {
      name: "Description",
      content: $page?.aboutUs?.metaDescription
    }
  ],
  link: [
    {
      rel: "canonical",
      href: UtilityService.formatCanonicalUrl(
        `${config.public.siteUrl}${$page?.aboutUs?.detailPage}`
      )
    }
  ],
  script: [
    useSchemaService().generateAboutUsSchema($page?.aboutUs as unknown as AboutUs | undefined)
  ]
});

const getBgImage = (img: string) => {
  return { "background-image": `url('${img}')` };
};

const whyCardImages = [whyCard1, whyCard2, whyCard3, whyCard4];

const isRankingsPlaying = ref<boolean>(true);

const whyAsuoRankingItems = computed(() => {
  const cards = $page?.aboutUs?.whyAsuoCards;
  if (!cards) return [];
  return cards.map((card, index) => ({
    displayHighlight: true,
    highlightText: card?.title ?? "",
    title: htmlToText(card?.description ?? "", { wordwrap: false }),
    imageSource: whyCardImages[index] ?? card?.icon ?? "",
    imageSourceAlt: card?.iconAlt ?? ""
  }));
});

const handlePlayPauseBtnClick = (num: number) => {
  isRankingsPlaying.value = !isRankingsPlaying.value;
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    whyAsuoRankingItems.value[num]?.highlightText.toLowerCase() ?? "",
    isRankingsPlaying.value ? "play button" : "pause button"
  );
};

const handleCarouselChevronClick = (num: number, direction: "PREV" | "NEXT") => {
  isRankingsPlaying.value = true;
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "carousel",
    "main content",
    whyAsuoRankingItems.value[num]?.highlightText.toLowerCase() ?? "",
    direction === "PREV" ? "left chevron" : "right chevron"
  );
};

const handleAccreditationCtaClick = (event: Event) => {
  event.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    "asu online accreditation information",
    "learn more about accreditation"
  );
  if (import.meta.client) {
    const accreditationLink = $page?.aboutUs?.accreditation?.link?.uri;
    navigateTo(accreditationLink, {
      open: {
        target: "_self"
      }
    });
  }
};

const handleVideoModalOpen = () => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "open",
    "click",
    "main content",
    $page?.aboutUs?.dedicatedToSuccess?.title?.toLowerCase() ?? "",
    "play button"
  );
};

const handleVideoModalClose = () => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "close",
    "click",
    "main content",
    $page?.aboutUs?.dedicatedToSuccess?.title?.toLowerCase() ?? "",
    "close cross"
  );
};

const handleEbookBtnClick = (event: EbookClickEventPayload) => {
  event.event.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    "interested in learning more about asu online?",
    $page?.aboutUs?.ebookButton?.text?.toLowerCase() ?? ""
  );
  if (window) window.open(event.url, "_blank");
};

const handleEbookSectionLinkClick = (Event: Event) => {
  Event.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    $page?.aboutUs?.ebookSectionTitle?.toLowerCase() ?? "",
    $page?.aboutUs?.ebookButton?.text?.toLowerCase() ?? ""
  );
  if (window) window.open($page?.aboutUs?.ebookSectionCta?.uri, "_blank");
};

const formatLinkTarget = (target: string) => {
  return UtilityService.formatLinkTarget(target);
};

const handleInternalLinkCtaClick = (
  event: Event,
  title: string,
  ctaText: string,
  ctaLink: string
) => {
  event.preventDefault();

  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    title.toLowerCase(),
    ctaText.toLowerCase()
  );
  if (import.meta.client) {
    navigateTo(ctaLink, {
      open: {
        target: "_self"
      }
    });
  }
};

const onTabChange = (event: { tabIndex: number }) => {
  const tabsData = sectionTabsData.value as { tabs: { title: string }[] };
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    "how does asu online work?",
    tabsData.tabs[event.tabIndex].title,
    "tabbed panel"
  );
};

const getVideoSource = computed(() => {
  return `https://www.youtube.com/embed/${$page?.aboutUs?.dedicatedToSuccess?.youtubeId}`;
});
</script>

<style lang="scss" scoped>
.asu-online-like-bg {
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center top;
}

#why-choose-asuo :deep(.cia-bg-gradient) {
  background-color: transparent;
}

.card-icon-wrap {
  :deep(.card-icon-height) {
    height: 54px;
  }
}

:deep(.intro-section .container) {
  background-color: var(--rds-light-3) !important;
}

.cards-wrap-more {
  max-height: 0;
  height: auto;
  overflow: hidden;
  opacity: 0;
  transition: max-height 0.4s ease-out;
}

.building-future-hero-img {
  width: 100%;
  height: 186px;
}

.section-hero {
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center top;

  .section-hero-content {
    height: 400px;
  }
}
.asu-online-like-img {
  width: auto;
  height: 280px;
}

.wrap-text {
  overflow-wrap: break-word;
}

.section-bg {
  position: relative;
  background-size: auto;
  background-position: top;
}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
  .building-future {
    background: var(--rds-dark-3);
    background: linear-gradient(
      var(--rds-dark-3) 0%,
      var(--rds-dark-3) 25%,
      var(--rds-dark-3) 25%,
      var(--rds-dark-3) 100%
    );
  }
  .building-future-hero-img {
    height: 342px;
  }
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .section-hero {
    .section-hero-content {
      height: 540px;
    }
  }

  .asu-online-like-img {
    height: 450px;
  }

  .building-future {
    background: var(--rds-dark-3);
    background: linear-gradient(
      var(--rds-white) 0%,
      var(--rds-white) 25%,
      var(--rds-dark-3) 25%,
      var(--rds-dark-3) 100%
    );
  }
  .building-future-hero-img {
    height: 550px;
  }
}
</style>
